import { useEffect, useRef, useState } from "react";
import AsyncSelect from "react-select/async";
import { Loader } from "@googlemaps/js-api-loader";
// helpers
import { type AutocompletionRequest, type PlaceOption, fetchPlacePredictions } from "./fetchPlacePredictions";

interface GooglePlacesAutocompleteProps {
  apiKey?: string;
  autocompletionRequest?: AutocompletionRequest;
  debounce?: number;
  selectProps?: Record<string, any>;
}

const isPlacesLibraryLoaded = () => Boolean(window.google?.maps?.places?.AutocompleteSuggestion);

/**
 * Google Places address search built on react-select. Replaces the react-google-places-autocomplete
 * package, which still calls the legacy AutocompleteService, and keeps its props and option shape.
 */
export default function GooglePlacesAutocomplete({
  apiKey = "",
  autocompletionRequest = {},
  debounce = 300,
  selectProps = {}
}: GooglePlacesAutocompleteProps) {
  const [isPlacesReady, setIsPlacesReady] = useState(isPlacesLibraryLoaded);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (isPlacesReady || !apiKey) {
      return;
    }

    new Loader({ apiKey, libraries: ["places"] })
      .importLibrary("places")
      .then(() => setIsPlacesReady(isPlacesLibraryLoaded()))
      .catch(error => console.error("[GooglePlacesAutocomplete] Could not load the Google Maps script", error));
  }, [apiKey, isPlacesReady]);

  useEffect(() => () => clearTimeout(debounceTimerRef.current), []);

  // Only the last call within the debounce window reaches Google, as with the previous package.
  const loadOptions = (inputValue: string, callback: (options: PlaceOption[]) => void) => {
    clearTimeout(debounceTimerRef.current);

    if (!isPlacesReady) {
      callback([]);
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchPlacePredictions(autocompletionRequest, inputValue).then(callback);
    }, debounce);
  };

  return (
    <AsyncSelect
      {...selectProps}
      loadOptions={loadOptions}
      getOptionValue={(option: PlaceOption) => option.value.place_id}
    />
  );
}
